import { useState, useEffect } from "react";
import axios from "axios";
import { 
  CheckCircle, Clock, Pause, XCircle, TrendingUp,
  Search, RefreshCw, ArrowLeft, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROCESS_LABELS = [
  { key: 'process1', label: 'P1', name: 'Corrugation' },
  { key: 'process2', label: 'P2', name: 'Sheet Cutting' },
  { key: 'process3', label: 'P3', name: 'Sheet Pasting' },
  { key: 'process4', label: 'P4', name: 'Slitter Scorer' },
  { key: 'process5', label: 'P5', name: 'Rotary Creasing' },
  { key: 'process6', label: 'P6', name: 'Printing & Slotting' },
  { key: 'process6a', label: 'P6A', name: 'Eccentric Slotter' },
  { key: 'process7', label: 'P7', name: 'Flat Bed Punch' },
  { key: 'process8', label: 'P8', name: 'Rotary Die Punch' },
  { key: 'process9', label: 'P9', name: 'Calico Pasting' },
  { key: 'process10', label: 'P10', name: 'Stitching' },
  { key: 'process11', label: 'P11', name: 'Box Packing' },
  { key: 'process12', label: 'P12', name: 'Dispatch' },
];

const WorkOrderProgressMobile = () => {
  const [progressData, setProgressData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedWO, setExpandedWO] = useState(null);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/reports/work-order-progress`);
      setProgressData(res.data || []);
    } catch (err) {
      console.error("Error fetching progress:", err);
      toast.error("Failed to load progress data");
    } finally {
      setLoading(false);
    }
  };

  const getCompletionData = (wo) => {
    if (wo.type !== 'new') return { percentage: 0, completedCount: 0, totalCount: 0 };

    const selectedProcesses = [];
    if (wo.processes) {
      for (let i = 1; i <= 12; i++) {
        if (wo.processes[`process${i}`]) selectedProcesses.push(`process${i}`);
      }
    }
    const total = selectedProcesses.length;
    let completed = 0;

    if (wo.process_work_status) {
      selectedProcesses.forEach(pk => {
        if (wo.process_work_status[pk]?.completed_count > 0) completed++;
      });
    }
    const fromArray = (wo.completed_processes || []).filter(cp => selectedProcesses.includes(cp)).length;
    completed = Math.max(completed, fromArray);

    return {
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
      completedCount: completed,
      totalCount: total
    };
  };

  const filtered = progressData.filter(wo => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      wo.work_order_number?.toLowerCase().includes(q) ||
      wo.customer_name?.toLowerCase().includes(q) ||
      wo.item_name?.toLowerCase().includes(q)
    );
  });

  const getProgressColor = (pct) => {
    if (pct >= 100) return '#22c55e';
    if (pct >= 50) return '#06b6d4';
    if (pct > 0) return '#f59e0b';
    return '#6b7280';
  };

  return (
    <div className="min-h-screen" style={{ background: "radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)" }}>
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/90 backdrop-blur-sm border-b border-emerald-500/20 px-4 py-3">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="wo-progress-back-btn" className="p-1.5 rounded-lg bg-gray-800 border border-gray-700">
            <ArrowLeft className="h-5 w-5 text-emerald-400" />
          </Link>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-emerald-400 tracking-wide" data-testid="wo-progress-title">
              WO Progress
            </h1>
            <p className="text-[11px] text-gray-500">{filtered.length} work orders</p>
          </div>
          <button
            onClick={fetchProgress}
            data-testid="wo-progress-refresh-btn"
            className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 active:bg-emerald-500/20"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search */}
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search WO number, customer, item..."
            data-testid="wo-progress-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-900/80 border border-gray-700 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500/50"
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-3 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 text-emerald-400 animate-spin mx-auto mb-3" />
              <p className="text-gray-400 text-sm">Loading work orders...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <TrendingUp className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">
              {searchQuery ? "No work orders match your search" : "No work orders found"}
            </p>
          </div>
        ) : (
          filtered.map((wo) => {
            const { percentage, completedCount, totalCount } = getCompletionData(wo);
            const isNew = wo.type === 'new';
            const isExpanded = expandedWO === wo.work_order_number;
            const progressColor = getProgressColor(percentage);

            return (
              <div
                key={wo.work_order_number}
                data-testid={`wo-card-${wo.work_order_number}`}
                className="bg-gray-900/60 border border-gray-700/50 rounded-xl overflow-hidden"
              >
                {/* Card Header - Always visible */}
                <button
                  onClick={() => setExpandedWO(isExpanded ? null : wo.work_order_number)}
                  className="w-full text-left px-4 py-3 active:bg-gray-800/50"
                  data-testid={`wo-expand-${wo.work_order_number}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-emerald-400 font-bold text-sm tracking-wide">
                      {wo.work_order_number}
                    </span>
                    <div className="flex items-center gap-2">
                      {isNew && (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          wo.status === 'active'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : wo.status === 'completed'
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                        }`}>
                          {wo.status || 'active'}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Customer & Item */}
                  <p className="text-white text-sm font-medium truncate">
                    {wo.item_name || "-"}
                  </p>
                  <p className="text-gray-500 text-xs truncate mt-0.5">
                    {wo.customer_name || "-"}
                  </p>

                  {/* Progress Bar */}
                  {isNew && (
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%`, backgroundColor: progressColor }}
                        />
                      </div>
                      <span className="text-xs font-bold min-w-[40px] text-right" style={{ color: progressColor }}>
                        {percentage}%
                      </span>
                    </div>
                  )}
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-gray-700/50 pt-3 space-y-3">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-2">
                      {wo.required_quantity && (
                        <div className="bg-gray-800/50 rounded-lg px-3 py-2 text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Qty</p>
                          <p className="text-white text-sm font-bold">{wo.required_quantity}</p>
                        </div>
                      )}
                      {wo.delivery_date && (
                        <div className="bg-gray-800/50 rounded-lg px-3 py-2 text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Delivery</p>
                          <p className="text-white text-sm font-bold">{wo.delivery_date}</p>
                        </div>
                      )}
                      {isNew && (
                        <div className="bg-gray-800/50 rounded-lg px-3 py-2 text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Processes</p>
                          <p className="text-white text-sm font-bold">{completedCount}/{totalCount}</p>
                        </div>
                      )}
                    </div>

                    {/* Timesheet Summary */}
                    {isNew && wo.timesheet_summary && wo.timesheet_summary.total_entries > 0 && (
                      <div className="flex items-center gap-3 text-xs">
                        {wo.timesheet_summary.completed_entries > 0 && (
                          <span className="flex items-center gap-1 text-green-400">
                            <CheckCircle className="h-3 w-3" />
                            {wo.timesheet_summary.completed_entries} done
                          </span>
                        )}
                        {wo.timesheet_summary.in_progress_entries > 0 && (
                          <span className="flex items-center gap-1 text-cyan-400">
                            <Clock className="h-3 w-3" />
                            {wo.timesheet_summary.in_progress_entries} in progress
                          </span>
                        )}
                        {wo.timesheet_summary.total_hours_worked > 0 && (
                          <span className="text-gray-400">
                            {wo.timesheet_summary.total_hours_worked}h total
                          </span>
                        )}
                      </div>
                    )}

                    {/* Process Pills */}
                    {isNew && (
                      <div className="flex flex-wrap gap-1.5">
                        {PROCESS_LABELS.filter(p => wo.processes && wo.processes[p.key]).map(p => {
                          const ws = wo.process_work_status && wo.process_work_status[p.key];
                          const done = ws?.completed_count > 0;
                          const ongoing = ws?.ongoing_count > 0;

                          return (
                            <div
                              key={p.key}
                              data-testid={`process-pill-${p.key}`}
                              className={`px-2 py-1 rounded-md text-[10px] font-semibold border ${
                                done
                                  ? 'bg-green-500/20 text-green-300 border-green-500/40'
                                  : ongoing
                                  ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                                  : 'bg-gray-800 text-gray-500 border-gray-700'
                              }`}
                            >
                              {done ? '✓ ' : ongoing ? '◎ ' : '○ '}{p.label}
                              <span className="ml-1 text-[9px] opacity-70">{p.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Legacy WO info */}
                    {!isNew && wo.machine_progress && wo.machine_progress.length > 0 && (
                      <div className="space-y-2">
                        {wo.machine_progress.map((mp, idx) => (
                          <div key={idx} className="bg-gray-800/50 rounded-lg px-3 py-2">
                            <p className="text-emerald-400 text-xs font-bold">{mp.machine_name}</p>
                            <p className="text-gray-400 text-[10px]">
                              {mp.total_hours?.toFixed(1)}h | {mp.entries?.length || 0} entries
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WorkOrderProgressMobile;
